import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, FileText, CheckCircle2, Clock, AlertCircle, X, 
  Upload, Image as ImageIcon, Video, File, Eye, Copy, Edit2, 
  Filter, Sparkles, Palette, Building2, Type, Mic, Square, Tag, ArrowRight , Lightbulb,
  Trash2, Archive, ArchiveRestore, RefreshCw
} from 'lucide-react';
import api, { deleteTemplate, archiveTemplate, unarchiveTemplate, syncTemplatesFromMeta } from '../services/api';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const statusStyles = {
  approved: { bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle2, label: 'Aprovado' },
  pending: { bg: 'bg-yellow-50', text: 'text-yellow-700', icon: Clock, label: 'Pendente' },
  rejected: { bg: 'bg-red-50', text: 'text-red-700', icon: AlertCircle, label: 'Rejeitado' },
  submitted: { bg: 'bg-blue-50', text: 'text-blue-700', icon: Clock, label: 'Enviado' },
  deleted: { bg: 'bg-red-50', text: 'text-red-700', icon: Trash2, label: 'Excluído na Meta' },
};

// ============================================
// COMPONENTE CARD DO TEMPLATE
// ============================================
const TemplateRow = ({ template, isSelected, onToggleSelect, onView, onDuplicate, onDelete, onArchive, onUnarchive }) => {
  const style = statusStyles[template.status] || statusStyles.pending;
  const StatusIcon = style.icon;

  return (
    <tr className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-green-50/60' : ''}`}>
      <td className="px-3 py-3 w-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(template.id)}
          className="w-4 h-4 text-green-600 rounded cursor-pointer"
        />
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="p-1.5 bg-gray-50 rounded-lg text-gray-400 flex-shrink-0">
            <FileText size={16} />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-gray-900 text-sm truncate">{template.display_name || template.name}</p>
            {template.body && (
              <p className="text-xs text-gray-400 truncate max-w-xs">{template.body}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">{template.category || 'Sem categoria'}</td>
      <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">{template.language}</td>
      <td className="px-3 py-3">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase whitespace-nowrap ${style.bg} ${style.text}`}>
          <StatusIcon size={12} />
          {style.label}
        </span>
      </td>
      <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">
        {template.updated_at ? new Date(template.updated_at).toLocaleDateString('pt-BR') : '-'}
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={() => onView(template)}
            title="Ver"
            className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => onDuplicate(template)}
            title="Duplicar"
            className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
          >
            <Copy size={16} />
          </button>
          {template.is_archived ? (
            <button
              onClick={() => onUnarchive(template)}
              title="Desarquivar (só no nosso sistema, não mexe na Meta)"
              className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 transition-colors"
            >
              <ArchiveRestore size={16} />
            </button>
          ) : (
            <button
              onClick={() => onArchive(template)}
              title="Arquivar (só no nosso sistema, não mexe na Meta)"
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <Archive size={16} />
            </button>
          )}
          <button
            onClick={() => onDelete(template)}
            title="Excluir de verdade, inclusive na Meta"
            className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
};    
// ================================================
// COMPONENTE PRINCIPAL TEMPLATES
// ============================================
function Templates() {
  const navigate = useNavigate();
  
  // ============================================
  // ESTADOS PRINCIPAIS
  // ============================================
  const [templates, setTemplates] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  const [viewingTemplateImage, setViewingTemplateImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState('');
  
  // Estados para variáveis
  const [variables, setVariables] = useState([]);
  const [showVariableSuggestions, setShowVariableSuggestions] = useState(false);

  // Estados para mídia (criação)
  const [headerImage, setHeaderImage] = useState(null);
  const [headerVideo, setHeaderVideo] = useState(null);
  const [headerDocument, setHeaderDocument] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);

  // Estados para filtros
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    headerType: '',
    status: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Estados para IA
  const [showAiGenerateModal, setShowAiGenerateModal] = useState(false);
  const [aiGenerateForm, setAiGenerateForm] = useState({
    campaign_type: '',
    business_type: '',
    style: '',
    colors: [],
    main_text: '',
    additional_elements: ''
  });
  const [generating, setGenerating] = useState(false);

  // Estados para gravação de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [audioTranscript, setAudioTranscript] = useState('');
  const [recognition, setRecognition] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingInterval, setRecordingInterval] = useState(null);

  const [form, setForm] = useState({
    name: '',
    display_name: '',
    categoria: 'Utility',
    linguagem: 'pt_BR',
    cabecalho: '',
    corpo: '',
    rodape: '',
    business_id: '',
  });
  const [businesses, setBusinesses] = useState([]); // ⭐ NOVO: empresas/WABAs conectadas
  const [activeSectorTab, setActiveSectorTab] = useState(null); // ⭐ NOVO: filtro por setor
  const [activeNumberFilter, setActiveNumberFilter] = useState(null); // ⭐ NOVO: filtro por número, dentro do setor ativo
   
  const [buttonType, setButtonType] = useState('none'); // 'none' | 'quick_reply' | 'cta'
  const [buttons, setButtons] = useState([]);

  const addButton = (type) => {
    if (type === 'quick_reply' && buttons.filter(b => b.type === 'QUICK_REPLY').length >= 3) {
      alert('Máximo de 3 botões de Resposta Rápida');
      return;
    }
    if (type === 'phone' && buttons.filter(b => b.type === 'PHONE_NUMBER').length >= 1) {
      alert('Máximo de 1 botão de Telefone');
      return;
    }
    if (type === 'url' && buttons.filter(b => b.type === 'URL').length >= 2) {
      alert('Máximo de 2 botões de Link');
      return;
    }
    if (type === 'quick_reply') setButtons([...buttons, { type: 'QUICK_REPLY', text: '' }]);
    if (type === 'phone') setButtons([...buttons, { type: 'PHONE_NUMBER', text: '', phone_number: '' }]);
    if (type === 'url') setButtons([...buttons, { type: 'URL', text: '', url: '' }]);
  };

  const updateButton = (index, field, value) => {
    const updated = [...buttons];
    updated[index] = { ...updated[index], [field]: value };
    setButtons(updated);
  };

  const removeButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };
  // ============================================
  // BUSCAR TEMPLATES
  // ============================================
  const fetchTemplates = async () => {
    try {
      const res = await api.get('/api/templates');
      setTemplates(res.data);
    } catch (err) {
      console.error('Erro ao buscar templates', err);
    }
  };

  useEffect(() => {
    fetchTemplates();
    api.get('/api/phone-numbers').then(res => {
      setBusinesses(res.data);
      const firstSector = res.data.find(n => n.sector_id);
      if (firstSector) setActiveSectorTab(firstSector.sector_id);
    }).catch(err => console.error('Erro ao buscar números', err));
  }, []);

  // Carregar variáveis disponíveis
  useEffect(() => {
    const loadVariables = async () => {
      try {
        const res = await api.get('/api/template-variables');
        setVariables(res.data);
      } catch (err) {
        console.error('Erro ao carregar variáveis:', err);
      }
    };
    loadVariables();
  }, []);

  // ============================================
  // 🎙️ INICIALIZAR RECONHECIMENTO DE VOZ
  // ============================================
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = 'pt-BR';
        
        recognitionInstance.onresult = (event) => {
          let finalTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript + ' ';
            }
          }
          
          if (finalTranscript) {
            setAudioTranscript(prev => prev + finalTranscript);
          }
        };
        
        recognitionInstance.onerror = (event) => {
          console.error('Erro no reconhecimento:', event.error);
          setIsRecording(false);
          if (event.error === 'not-allowed') {
            alert('Permissão de microfone negada. Habilite o microfone nas configurações do navegador.');
          }
        };
        
        recognitionInstance.onend = () => {
          setIsRecording(false);
        };
        
        setRecognition(recognitionInstance);
      } else {
        console.warn('Reconhecimento de voz não suportado neste navegador');
      }
    }
    
    return () => {
      if (recordingInterval) {
        clearInterval(recordingInterval);
      }
    };
  }, []);

  // ============================================
  // 🎙️ INICIAR GRAVAÇÃO
  // ============================================
  const startRecording = () => {
    if (!recognition) {
      alert('Seu navegador não suporta reconhecimento de voz. Use Chrome ou Edge.');
      return;
    }
    
    setAudioTranscript('');
    setRecordingTime(0);
    setIsRecording(true);
    
    try {
      recognition.start();
      
      const interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      setRecordingInterval(interval);
    } catch (err) {
      console.error('Erro ao iniciar gravação:', err);
      setIsRecording(false);
    }
  };

  // ============================================
  // 🎙️ PARAR GRAVAÇÃO
  // ============================================
  const stopRecording = () => {
    if (recognition) {
      try {
        recognition.stop();
      } catch (err) {
        console.error('Erro ao parar gravação:', err);
      }
    }
    setIsRecording(false);
    if (recordingInterval) {
      clearInterval(recordingInterval);
      setRecordingInterval(null);
    }
  };

  // ============================================
  // 🧹 LIMPAR CAMPOS DE IA
  // ============================================
  const clearAiFields = () => {
    console.log(' Limpando campos de IA...');
    setAiGenerateForm({
      campaign_type: '',
      business_type: '',
      style: '',
      colors: [],
      main_text: '',
      additional_elements: ''
    });
    setAudioTranscript('');
    if (isRecording) {
      stopRecording();
    }
  };

  // ============================================
  // ⭐ INTERPRETAR ÁUDIO E PREENCHER CAMPOS
  // ============================================
  const interpretAudioAndFill = () => {
    if (!audioTranscript.trim()) {
      alert('Nenhuma fala detectada. Tente novamente.');
      return;
    }

    console.log('🎤 Iniciando interpretação do áudio...');
    console.log('Texto completo:', audioTranscript);

    const text = audioTranscript.toLowerCase();
    const newForm = { ...aiGenerateForm };

    // ============================================
    // 1. DETECTAR TIPO DE CAMPANHA (com palavras compostas)
    // ============================================
    const campaignPatterns = [
      { keywords: ['páscoa', 'pascoa'], value: 'Promoção de Páscoa' },
      { keywords: ['natal'], value: 'Promoção de Natal' },
      { keywords: ['ano novo', 'anovo'], value: 'Promoção de Ano Novo' },
      { keywords: ['black friday'], value: 'Black Friday' },
      { keywords: ['aniversário', 'aniversario'], value: 'Aniversário' },
      { keywords: ['liquidação', 'liquidacao'], value: 'Liquidação' },
      { keywords: ['promoção', 'promocao'], value: 'Promoção' },
      { keywords: ['lançamento', 'lancamento'], value: 'Lançamento' },
      { keywords: ['oferta'], value: 'Oferta Especial' },
      { keywords: ['desconto'], value: 'Oferta Especial' },
      { keywords: ['evento'], value: 'Evento' }
    ];

    for (const pattern of campaignPatterns) {
      for (const keyword of pattern.keywords) {
        if (text.includes(keyword)) {
          newForm.campaign_type = pattern.value;
          console.log('✅ Campanha detectada:', pattern.value);
          break;
        }
      }
      if (newForm.campaign_type) break;
    }

    // ============================================
    // 2. DETECTAR NEGÓCIO/PRODUTO
    // ============================================
    const businessPatterns = [
      { keywords: ['dentista', 'odontologia', 'odontológico', 'odontologico', 'dente'], value: 'Consultório Odontológico' },
      { keywords: ['médico', 'medico', 'clínica', 'clinica'], value: 'Clínica Médica' },
      { keywords: ['restaurante', 'comida'], value: 'Restaurante' },
      { keywords: ['loja', 'roupa', 'moda'], value: 'Loja de Roupas' },
      { keywords: ['imóvel', 'imovel', 'casa', 'apartamento'], value: 'Imobiliária' },
      { keywords: ['academia', 'fitness'], value: 'Academia' },
      { keywords: ['salão', 'salao', 'cabelo', 'beleza'], value: 'Salão de Beleza' },
      { keywords: ['pet', 'cachorro', 'gato', 'animal'], value: 'Pet Shop' },
      { keywords: ['farmácia', 'farmacia', 'remédio', 'remedio'], value: 'Farmácia' },
      { keywords: ['barbearia', 'barbeiro'], value: 'Barbearia' }
    ];

    for (const pattern of businessPatterns) {
      for (const keyword of pattern.keywords) {
        if (text.includes(keyword)) {
          newForm.business_type = pattern.value;
          console.log('✅ Negócio detectado:', pattern.value);
          break;
        }
      }
      if (newForm.business_type) break;
    }

    // ============================================
    // 3. DETECTAR CORES
    // ============================================
    const colorPatterns = [
      { keywords: ['azul'], value: 'Azul' },
      { keywords: ['vermelho', 'vermelha'], value: 'Vermelho' },
      { keywords: ['verde'], value: 'Verde' },
      { keywords: ['amarelo', 'amarela'], value: 'Amarelo' },
      { keywords: ['roxo', 'roxa'], value: 'Roxo' },
      { keywords: ['rosa'], value: 'Rosa' },
      { keywords: ['laranja'], value: 'Laranja' },
      { keywords: ['preto', 'preta'], value: 'Preto' },
      { keywords: ['branco', 'branca'], value: 'Branco' },
      { keywords: ['dourado', 'dourada'], value: 'Dourado' },
      { keywords: ['prateado', 'prateada'], value: 'Prateado' }
    ];

    const detectedColors = [];
    for (const pattern of colorPatterns) {
      for (const keyword of pattern.keywords) {
        if (text.includes(keyword) && !detectedColors.includes(pattern.value)) {
          detectedColors.push(pattern.value);
        }
      }
    }
    if (detectedColors.length > 0) {
      newForm.colors = detectedColors;
      console.log('✅ Cores detectadas:', detectedColors);
    }

    // ============================================
    // 4. DETECTAR ESTILO
    // ============================================
    const stylePatterns = [
      { keywords: ['moderno', 'moderna'], value: 'Moderno e profissional' },
      { keywords: ['profissional'], value: 'Moderno e profissional' },
      { keywords: ['minimalista', 'minimal'], value: 'Minimalista e clean' },
      { keywords: ['clean', 'limpo', 'limpa'], value: 'Minimalista e clean' },
      { keywords: ['simples'], value: 'Minimalista e clean' },
      { keywords: ['colorido', 'colorida', 'vibrante'], value: 'Colorido e vibrante' },
      { keywords: ['elegante', 'sofisticado', 'luxo'], value: 'Elegante e sofisticado' },
      { keywords: ['divertido', 'descontraído', 'descontraido'], value: 'Divertido e descontraído' }
    ];

    for (const pattern of stylePatterns) {
      for (const keyword of pattern.keywords) {
        if (text.includes(keyword)) {
          newForm.style = pattern.value;
          console.log('✅ Estilo detectado:', pattern.value);
          break;
        }
      }
      if (newForm.style) break;
    }

    // ============================================
    // 5. EXTRAIR TEXTO PRINCIPAL (melhorado)
    // ============================================
    const textExtractionPatterns = [
      /(?:com o texto|dizendo|escreva|fale|texto)\s+(?:["']?)([^"']{5,100})(?:["']?)/i,
      /(?:para|sobre)\s+(?:["']?)([^"']{5,100})(?:["']?)(?:\s+(?:com|em|para|e|cores))/i,
      /(?:dentes?|sorriso)\s+(?:brancos?|saudáveis?|perfeitos?|bonitos?)/i
    ];

    for (const pattern of textExtractionPatterns) {
      const match = text.match(pattern);
      if (match && match[0]) {
        const extractedText = match[0].trim();
        newForm.main_text = extractedText.charAt(0).toUpperCase() + extractedText.slice(1);
        console.log('✅ Texto principal extraído:', newForm.main_text);
        break;
      }
    }

    if (!newForm.main_text) {
      const businessMatch = text.match(/(?:odontologia|dentista|clínica|loja|restaurante)\s+([^,\.]{5,80})/i);
      if (businessMatch && businessMatch[1]) {
        const description = businessMatch[1].trim();
        let cleanDescription = description;
        for (const color of detectedColors) {
          cleanDescription = cleanDescription.replace(new RegExp(color.toLowerCase(), 'gi'), '');
        }
        cleanDescription = cleanDescription.replace(/(promoção|promocao|natal|páscoa|pascoa)/gi, '').trim();
        
        if (cleanDescription.length > 3) {
          newForm.main_text = cleanDescription.charAt(0).toUpperCase() + cleanDescription.slice(1);
          console.log('✅ Descrição extraída do contexto:', newForm.main_text);
        }
      }
    }

    console.log('📋 Formulário preenchido:', newForm);
    setAiGenerateForm(newForm);
    
    setTimeout(() => {
      alert('✅ Áudio interpretado com sucesso!\n\nCampos preenchidos:\n' +
            `• Campanha: ${newForm.campaign_type || '—'}\n` +
            `• Negócio: ${newForm.business_type || '—'}\n` +
            `• Cores: ${newForm.colors.join(', ') || '—'}\n` +
            `• Estilo: ${newForm.style || '—'}\n` +
            `• Texto: ${newForm.main_text || '—'}\n\n` +
            'Revise os campos e clique em "Gerar Imagem".');
    }, 100);
  };

  // Formatador de tempo
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // ============================================
  // ⭐ FILTRAR TEMPLATES
  // ============================================
  const applyFilters = (templateList) => {
    return templateList.filter(template => {
      if (filters.dateFrom) {
        const templateDate = new Date(template.created_at);
        const fromDate = new Date(filters.dateFrom);
        if (templateDate < fromDate) return false;
      }
      
      if (filters.dateTo) {
        const templateDate = new Date(template.created_at);
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (templateDate > toDate) return false;
      }
      
      if (filters.headerType) {
        const templateHeader = template.header || 'TEXT';
        if (templateHeader !== filters.headerType) return false;
      }

      if (filters.status) {
        if (template.status !== filters.status) return false;
      }
      
      return true;
    });
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      headerType: '',
      status: '',
    });
  };

  const hasActiveFilters = filters.dateFrom || filters.dateTo || filters.headerType || filters.status;

  // ⭐ NOVO: mapeia waba_id -> setor, pra filtrar templates pelas abas de setor
  const wabaToSectorId = {};
  businesses.forEach(n => { if (n.waba_id && n.sector_id) wabaToSectorId[n.waba_id] = n.sector_id; });
  const sectorTabs = Array.from(
    new Map(businesses.filter(n => n.sector_id).map(n => [n.sector_id, { id: n.sector_id, name: n.sector_name }])).values()
  );

  const baseTemplates = (showArchived
    ? templates.filter(t => t.is_archived)
    : templates.filter(t => !t.is_archived)
  ).filter(t => activeSectorTab == null || wabaToSectorId[t.waba_id] === activeSectorTab)
   .filter(t => activeNumberFilter == null || t.waba_id === activeNumberFilter);

  const filtered = applyFilters(
    search
      ? baseTemplates.filter((t) =>
          t.name.toLowerCase().includes(search.toLowerCase()) ||
          (t.display_name && t.display_name.toLowerCase().includes(search.toLowerCase()))
        )
      : baseTemplates
  );

  // ============================================
  // ⭐ GERAR IMAGEM COM IA
  // ============================================
  const handleGenerateWithAi = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/api/ai/generate', aiGenerateForm);
      
      if (res.data.success) {
        const imageUrl = res.data.image_url;
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new window.File([blob], res.data.filename, { type: 'image/png' });
        
        setHeaderImage(file);
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
        
        setShowAiGenerateModal(false);
        if (isRecording) stopRecording();
        clearAiFields();
        alert('✅ Imagem gerada com sucesso!');
      }
    } catch (err) {
      alert('Erro ao gerar imagem: ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  // ============================================
  // FUNÇÃO AUXILIAR PARA VALORES DE EXEMPLO
  // ============================================
  const getExemploValor = (nomeVariavel) => {
    const exemplos = {
      'nome_cliente': 'João Silva',
      'nome_empresa': 'Soulidari',
      'data_atual': new Date().toLocaleDateString('pt-BR'),
      'hora_atual': new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      'link_agendamento': 'https://exemplo.com/agendar',
      'telefone_empresa': '(11) 99999-9999',
      'cpf_cliente': '123.456.789-00',
      'valor_boleto': 'R$ 97,00',
      'vencimento_boleto': '30/06/2026'
    };
    
    return exemplos[nomeVariavel] || `[${nomeVariavel}]`;
  };

  // ============================================
  // PREVIEW
  // ============================================
  const gerarPreview = () => {
    let texto = form.corpo;
    
    // Substituir variáveis
    for (const variable of variables) {
      const pattern = new RegExp(`{{${variable.name}}}`, 'g');
      const exemploValor = variable.default_value || getExemploValor(variable.name);
      texto = texto.replace(pattern, exemploValor);
    }
    
    // Substituir variáveis do Meta
    texto = texto.replace(/\{\{1\}\}/g, 'João');
    texto = texto.replace(/\{\{2\}\}/g, '123');
    
    if (form.cabecalho === 'TEXT' && form.cabecalho) {
      texto = form.cabecalho + '\n\n' + texto;
    }
    
    if (form.rodape) {
      texto = texto + '\n\n' + form.rodape;
    }
    
    setPreview(texto);
  };

  // Atualizar preview quando mudar
  useEffect(() => {
    gerarPreview();
  }, [form.cabecalho, form.corpo, form.rodape, variables]);

  // ============================================
  // INSERIR VARIÁVEL
  // ============================================
  const insertVariable = (variableName) => {
    const variableText = `{{${variableName}}}`;
    const textarea = document.querySelector('textarea[name="corpo"]');
    
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = form.corpo;
      const newText = text.substring(0, start) + variableText + text.substring(end);
      setForm({ ...form, corpo: newText });
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variableText.length, start + variableText.length);
      }, 0);
    }
  };

  // ============================================
  // HANDLERS DE MÍDIA
  // ============================================
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Imagem deve ter no máximo 5MB');
        return;
      }
      setHeaderImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 16 * 1024 * 1024) {
        alert('Vídeo deve ter no máximo 16MB');
        return;
      }
      setHeaderVideo(file);
      setVideoPreview(file.name);
    }
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Documento deve ter no máximo 10MB');
        return;
      }
      setHeaderDocument(file);
      setDocumentPreview(file.name);
    }
  };

  const clearMedia = () => {
    setHeaderImage(null);
    setHeaderVideo(null);
    setHeaderDocument(null);
    setImagePreview(null);
    setVideoPreview(null);
    setDocumentPreview(null);
  };

  // ============================================
  // VISUALIZAR TEMPLATE
  // ============================================
  const handleView = async (template) => {
    setViewingTemplate(template);
    
    if (template.media_path && template.header_type === 'IMAGE') {
      try {
        const filename = template.media_path.split(/[\\/]/).pop();
        const imageUrl = `${API}/uploads/templates/${filename}`;
        
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setViewingTemplateImage(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Erro ao carregar imagem:', err);
        setViewingTemplateImage(null);
      }
    } else {
      setViewingTemplateImage(null);
    }
    
    setShowViewModal(true);
  };


  // ============================================
  // EXCLUIR / ARQUIVAR / DESARQUIVAR / SINCRONIZAR
  // ============================================

  const handleDelete = async (template) => {
    if (!window.confirm(`Excluir o template "${template.display_name || template.name}"? Isso também remove ele da Meta.`)) return;
    try {
      await deleteTemplate(template.id);
      fetchTemplates();
    } catch (err) {
      alert('Erro ao excluir: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleArchive = async (template) => {
    try {
      await archiveTemplate(template.id);
      fetchTemplates();
    } catch (err) {
      alert('Erro ao arquivar: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleUnarchive = async (template) => {
    try {
      await unarchiveTemplate(template.id);
      fetchTemplates();
    } catch (err) {
      alert('Erro ao desarquivar: ' + (err.response?.data?.error || err.message));
    }
  };

  // ⭐ NOVO: seleção múltipla + ações em massa
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = (ids) => {
    setSelectedIds(prev => {
      const allSelected = ids.length > 0 && ids.every(id => prev.has(id));
      return allSelected ? new Set() : new Set(ids);
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if (!window.confirm(`Excluir ${selectedIds.size} template(s)? Isso também remove eles da Meta.`)) return;
    try {
      for (const id of selectedIds) {
        await deleteTemplate(id);
      }
      clearSelection();
      fetchTemplates();
    } catch (err) {
      alert('Erro ao excluir em massa: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleBulkArchive = async () => {
    try {
      for (const id of selectedIds) {
        await archiveTemplate(id);
      }
      clearSelection();
      fetchTemplates();
    } catch (err) {
      alert('Erro ao arquivar em massa: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleBulkUnarchive = async () => {
    try {
      for (const id of selectedIds) {
        await unarchiveTemplate(id);
      }
      clearSelection();
      fetchTemplates();
    } catch (err) {
      alert('Erro ao desarquivar em massa: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await syncTemplatesFromMeta();
      const { imported, updated, errors } = res.data;
      let msg = `✅ Sincronizado!\n\nImportados: ${imported}\nAtualizados: ${updated}`;
      if (errors && errors.length > 0) msg += `\n\nErros:\n${errors.join('\n')}`;
      alert(msg);
      fetchTemplates();
    } catch (err) {
      alert('Erro ao sincronizar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSyncing(false);
    }
  };
  



const extractFromComponents = (template) => {
  let components = template.components;
  if (typeof components === 'string') {
    try { components = JSON.parse(components); } catch { components = []; }
  }
  components = components || [];

  const headerComp = components.find(c => c.type === 'HEADER');
  const bodyComp = components.find(c => c.type === 'BODY');
  const footerComp = components.find(c => c.type === 'FOOTER');
  const buttonsComp = components.find(c => c.type === 'BUTTONS');

  return {
    header: headerComp?.format || '',        // 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'TEXT'
    headerText: headerComp?.format === 'TEXT' ? headerComp.text : '',
    body: bodyComp?.text || '',
    footer: footerComp?.text || '',
    buttons: buttonsComp?.buttons || [],
  };
};

  // ============================================
  // DUPLICAR TEMPLATE
  // ============================================
     const handleDuplicate = async (template) => {
  const { header, headerText, body, footer, buttons: btns } = extractFromComponents(template);
  setForm({
    name: template.name + '_copy',
    display_name: template.display_name ? template.display_name + ' (Cópia)' : '',
    categoria: template.category || 'Utility',
    linguagem: template.language || 'pt_BR',
    cabecalho: header,
    cabecalho_texto: headerText,
    corpo: body,
    rodape: footer,
  });
  setButtons(btns);
  clearMedia();

  // ⭐ NOVO: carrega a imagem original (prévia + anexo de verdade), pra duplicar
  // sem precisar subir o arquivo de novo à mão
  if (template.media_path && header === 'IMAGE') {
    try {
      const filename = template.media_path.split(/[\\/]/).pop();
      const imageUrl = `${API}/uploads/templates/${filename}`;
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const ext = (filename.split('.').pop() || '').toLowerCase();
      const tipoPorExt = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif' }[ext];
      const tipo = blob.type.startsWith('image/') ? blob.type : tipoPorExt;
      // ⭐ CORRIGIDO: além da prévia, anexa a imagem original de verdade. Sem isso o
      // botão "Criar Template" (que exige o arquivo) ficava desabilitado e a área de
      // envio sumia, porque a prévia já estava na tela. Se o arquivo não carregar
      // como imagem, não mostra prévia falsa: a área de envio continua disponível.
      // ⭐ CORRIGIDO: confere pelos primeiros bytes se o conteúdo é mesmo uma imagem
      // (PNG, JPEG, GIF ou WEBP). Só o tipo ou o nome do arquivo não bastam: o servidor
      // pode devolver uma página HTML com status 200, e ela seria anexada como "imagem".
      const cab = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
      const ehImagem =
        (cab[0] === 0x89 && cab[1] === 0x50 && cab[2] === 0x4E && cab[3] === 0x47) ||
        (cab[0] === 0xFF && cab[1] === 0xD8 && cab[2] === 0xFF) ||
        (cab[0] === 0x47 && cab[1] === 0x49 && cab[2] === 0x46 && cab[3] === 0x38) ||
        (cab[0] === 0x52 && cab[1] === 0x49 && cab[2] === 0x46 && cab[3] === 0x46 && cab[8] === 0x57 && cab[9] === 0x45 && cab[10] === 0x42 && cab[11] === 0x50);
      if (response.ok && tipo && ehImagem) {
        setHeaderImage(new window.File([blob], filename, { type: tipo }));
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(blob);
      } else {
        console.warn('Duplicar template: a imagem original não foi aceita (status ' + response.status + ', tipo ' + blob.type + ', parece imagem: ' + ehImagem + ')');
      }
    } catch (err) {
      console.error('Erro ao carregar imagem de referência:', err);
    }
  }

  setShowModal(true);
};
  
  // ============================================
  // CRIAR TEMPLATE
  // ============================================
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.corpo.trim()) {
      alert('Preencha nome e corpo da mensagem');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('display_name', form.display_name);
      formData.append('business_id', form.business_id);
      formData.append('categoria', form.categoria);
      formData.append('linguagem', form.linguagem);
      formData.append('cabecalho', form.cabecalho);
      formData.append('corpo', form.corpo);
      formData.append('rodape', form.rodape);
      if (buttons.length > 0) {
        formData.append('buttons', JSON.stringify(buttons));
      }
      if (form.cabecalho === 'IMAGE' && headerImage) {
        formData.append('header_image', headerImage);
      } else if (form.cabecalho === 'VIDEO' && headerVideo) {
        formData.append('header_video', headerVideo);
      } else if (form.cabecalho === 'DOCUMENT' && headerDocument) {
        formData.append('header_document', headerDocument);
      }

      const res = await api.post('/api/templates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.warnings && res.data.warnings.length > 0) {
        alert('⚠️ Template criado com avisos:\n\n' + res.data.warnings.join('\n'));
      } else {
        alert('✅ Template criado com sucesso!');
      }

      setShowModal(false);
      setForm({
        name: '', display_name: '', categoria: 'Utility',
       linguagem: 'pt_BR', cabecalho: '', corpo: '', rodape: '',
      });
      setButtonType('none');
      setButtons([]);
      clearMedia();
      fetchTemplates();
    } catch (err) {
      alert('Erro ao criar template: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // COMPONENTE DE PREVIEW WHATSAPP
  // ============================================
  const WhatsAppPreview = ({ template, preview, imagePreview, videoPreview, documentPreview, previewButtons = [] }) => {
    let previewText = preview || '';
    previewText = previewText.replace(/\{\{1\}\}/g, 'NomeCliente');
    previewText = previewText.replace(/\{\{2\}\}/g, '123');
    if (template.header === 'TEXT' && template.header) {
      previewText = template.header + '\n' + previewText;
    }
    if (template.footer) {
      previewText += '\n' + template.footer;
    }

    return (
      <div className="bg-white rounded-[1.5rem] border-[6px] border-gray-900 shadow-xl overflow-hidden mx-auto flex flex-col"
           style={{ width: '260px', height: '520px' }}>

        <div className="bg-[#008069] text-white px-3 py-1 flex items-center justify-between text-[9px] flex-shrink-0">
          <span>9:41</span>
          <div className="flex gap-0.5">
            <span></span><span>🔋</span>
          </div>
        </div>

        <div className="bg-[#008069] text-white px-2.5 py-2 flex items-center gap-2 flex-shrink-0">
          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <FileText size={12} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold truncate">Soulidari</p>
            <p className="text-[9px] opacity-80 truncate">online</p>
          </div>
        </div>

        <div className="flex-1 bg-[#efeae2] overflow-y-auto p-2"
             style={{
               backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d1d7db' fill-opacity='0.3'%3E%3Cpath d='M20 20v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")`
             }}>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden max-w-full">
            {imagePreview && (
              <div className="w-full bg-gray-100">
                <img src={imagePreview} alt="Header" className="w-full h-auto max-h-[140px] object-contain" />
              </div>
            )}
            {videoPreview && (
              <div className="w-full bg-gray-900 h-[120px] flex items-center justify-center relative">
                <div className="text-center text-white">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-1">
                    <span className="text-2xl">▶️</span>
                  </div>
                  <p className="text-[10px] px-2 truncate max-w-[200px]">{videoPreview}</p>
                </div>
              </div>
            )}
            {documentPreview && (
              <div className="w-full bg-gray-50 p-3 flex items-center gap-2">
                <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center flex-shrink-0">
                  <span className="text-red-600 text-xs font-bold">PDF</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-medium text-gray-900 truncate">{documentPreview}</p>
                  <p className="text-[9px] text-gray-500">Documento</p>
                </div>
              </div>
            )}

            <div className="p-2">
              <div className="text-[11px] text-gray-800 whitespace-pre-wrap leading-tight">
                {previewText || 'Sem conteúdo'}
              </div>
                            {template.footer && (
                <div className="mt-1 pt-1 border-t border-gray-100">
                  <p className="text-[9px] text-gray-500">{template.footer}</p>
                </div>
              )}
            </div>
            {previewButtons.length > 0 && (
              <div className="border-t border-gray-100">
                {previewButtons.map((btn, idx) => (
                  <div
                    key={idx}
                    className="text-[10px] text-blue-600 text-center py-1.5 border-t border-gray-100 first:border-t-0 font-medium"
                  >
                    {btn.type === 'PHONE_NUMBER' ? '📞 ' : btn.type === 'URL' ? '🔗 ' : ''}
                    {btn.text || '(sem texto)'}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end mt-0.5">
            <span className="text-[8px] text-gray-500 bg-white/60 px-1 py-0.5 rounded">
              {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })} ✓✓
            </span>
          </div>
        </div>

        <div className="bg-[#f0f2f5] px-2.5 py-1.5 flex items-center gap-1.5 flex-shrink-0">
          <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-gray-600 text-[9px]">+</span>
          </div>
          <div className="flex-1 bg-white rounded-full px-2.5 py-1 text-[10px] text-gray-400 shadow-sm">
            Mensagem
          </div>
          <div className="w-5 h-5 bg-[#008069] rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[9px]">🎤</span>
          </div>
        </div>

        <div className="bg-white px-3 py-1.5 flex justify-center flex-shrink-0">
          <div className="w-28 h-1 bg-gray-400 rounded-full"></div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Templates de Mensagem</h1>
          <p className="text-gray-500">Gerencie seus modelos de mensagem aprovados pela Meta.</p>
        </div>
        <div className="flex gap-3 flex-wrap">
        <button
  onClick={() => {
    console.log('🔵 Navegando para /template-variables');
    navigate('/template-variables');
  }}
  className="flex items-center gap-2 px-5 py-2.5 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold hover:bg-purple-200 transition-all"
>
  <Tag size={18} />
  Variáveis
  <ArrowRight size={16} />
</button>
          
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-full text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Sincronizando...' : 'Sincronizar com Meta'}
          </button>

          <button
            onClick={() => setShowArchived(!showArchived)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
              showArchived ? 'bg-amber-100 text-amber-700' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Archive size={18} />
            {showArchived ? 'Ver Ativos' : 'Ver Arquivados'}
          </button>

          <button
            onClick={() => {
              setForm({
                name: '', display_name: '', categoria: 'Utility',
                linguagem: 'pt_BR', cabecalho: '', corpo: '', rodape: '', business_id: '',
              });
              setButtonType('none');
              setButtons([]);
              clearMedia();
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 rounded-full text-sm font-semibold text-white hover:bg-green-700 shadow-lg shadow-green-200 transition-all"
          >
            <Plus size={18} />
            Novo Template
          </button>
        </div>
      </div>

      {/* ⭐ NOVO: abas de filtro por setor, estilo "aba de pasta" (igual Campanhas) */}
      {sectorTabs.length > 0 && (
        <div className="flex gap-2 flex-wrap -mb-px">
          {sectorTabs.map(s => (
            <button
              key={s.id}
              onClick={() => { setActiveSectorTab(s.id); setActiveNumberFilter(null); }}
              className={`px-6 py-2.5 text-base font-bold rounded-t-lg border transition-colors ${
                activeSectorTab === s.id
                  ? 'bg-white border-blue-400 border-b-white text-gray-900 z-10 relative'
                  : 'bg-transparent border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* ⭐ NOVO: filtro por número — só aparece quando o setor ativo tem mais de um número WhatsApp */}
      {(() => {
        const numbersOfActiveSector = businesses.filter(n => String(n.sector_id) === String(activeSectorTab));
        if (numbersOfActiveSector.length <= 1) return null;
        return (
          <div className="flex gap-2 flex-wrap items-center bg-white border border-gray-100 rounded-xl px-3 py-2 -mt-px relative z-0">
            <span className="text-xs text-gray-400 font-medium">Número:</span>
            <button
              onClick={() => setActiveNumberFilter(null)}
              className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                activeNumberFilter === null
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
              }`}
            >
              Todos
            </button>
            {numbersOfActiveSector.map(n => (
              <button
                key={n.waba_id}
                onClick={() => setActiveNumberFilter(n.waba_id)}
                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                  activeNumberFilter === n.waba_id
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                }`}
              >
                {n.display_phone_number || n.phone_number}
              </button>
            ))}
          </div>
        );
      })()}

      {/* Busca + Botão Filtros */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 outline-none transition-all"
          />
        </div>
        
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
            showFilters || hasActiveFilters
              ? 'bg-green-100 text-green-700 border border-green-300'
              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Filter size={16} />
          Filtros
          {hasActiveFilters && (
            <span className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></span>
          )}
        </button>
      </div>

      {/* Painel de Filtros */}
      {showFilters && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
              <Filter size={16} className="text-green-600" />
              Filtrar Templates
            </h3>
            <button
              onClick={clearFilters}
              className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
            >
              <X size={12} />
              Limpar filtros
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Data Inicial</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Data Final</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de Cabeçalho</label>
              <select
                value={filters.headerType}
                onChange={(e) => setFilters({...filters, headerType: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
              >
                <option value="">Todos os tipos</option>
                <option value="IMAGE">🖼️ Imagem</option>
                <option value="VIDEO"> Vídeo</option>
                <option value="DOCUMENT">📄 Documento</option>
                <option value="TEXT">📝 Texto</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
              >
                <option value="">Todos os status</option>
                <option value="approved">✅ Aprovado</option>
                <option value="pending">⏳ Pendente</option>
                <option value="submitted">📤 Enviado</option>
                <option value="rejected">❌ Rejeitado</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              <strong>{filtered.length}</strong> template(s) encontrado(s)
              {hasActiveFilters && <span className="text-green-600 ml-2">• Filtros ativos</span>}
            </p>
          </div>
        </div>
      )}

      {/* Lista de Templates */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border-b border-green-100">
            <span className="text-sm font-medium text-green-800">{selectedIds.size} modelo(s) selecionado(s)</span>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-red-200 text-red-700 rounded-lg text-xs font-medium hover:bg-red-50 transition-colors"
            >
              <Trash2 size={14} /> Excluir
            </button>
            <button
              onClick={handleBulkArchive}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors"
            >
              <Archive size={14} /> Arquivar
            </button>
            <button
              onClick={handleBulkUnarchive}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-amber-200 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-50 transition-colors"
            >
              <ArchiveRestore size={14} /> Desarquivar
            </button>
            <button
              onClick={clearSelection}
              className="ml-auto text-xs text-gray-400 hover:text-gray-600"
            >
              Limpar seleção
            </button>
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FileText size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">Nenhum template encontrado</p>
            <p className="text-sm mt-1">
              {hasActiveFilters ? 'Tente limpar os filtros' : 'Crie um novo template para começar'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filtered.length > 0 && filtered.every(t => selectedIds.has(t.id))}
                      onChange={() => toggleSelectAll(filtered.map(t => t.id))}
                      className="w-4 h-4 text-green-600 rounded cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase">Nome do modelo</th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase">Categoria</th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase">Idioma</th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase">Última edição</th>
                  <th className="px-3 py-3 text-xs font-bold text-gray-500 uppercase text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <TemplateRow
                    key={t.id}
                    template={t}
                    isSelected={selectedIds.has(t.id)}
                    onToggleSelect={toggleSelect}
                    onView={handleView}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    onArchive={handleArchive}
                    onUnarchive={handleUnarchive}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ============================================ */}
      {/* MODAL DE VISUALIZAÇÃO */}
      {/* ============================================ */}
            {showViewModal && viewingTemplate && (() => {
        const viewData = extractFromComponents(viewingTemplate);
        return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl p-6 relative shadow-xl max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowViewModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
            
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-lg font-bold">Detalhes do Template</h2>
                <p className="text-sm text-gray-500">{viewingTemplate.display_name || viewingTemplate.name}</p>
              </div>
              <button
                onClick={() => {
                  handleDuplicate(viewingTemplate);
                  setShowViewModal(false);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700"
              >
                <Copy size={16} />
                Duplicar
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-700 mb-3">Informações</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Nome:</span>
                      <span className="font-medium">{viewingTemplate.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Categoria:</span>
                      <span className="font-medium">{viewingTemplate.category || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Idioma:</span>
                      <span className="font-medium">{viewingTemplate.language}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Cabeçalho:</span>
                      <span className="font-medium">
                                                {viewData.header === 'IMAGE' ? '🖼️ Imagem' :
                         viewData.header === 'VIDEO' ? '🎥 Vídeo' :
                         viewData.header === 'DOCUMENT' ? '📄 Documento' :
                         viewData.header || 'Nenhum'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status:</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        statusStyles[viewingTemplate.status]?.bg || 'bg-gray-100'
                      } ${statusStyles[viewingTemplate.status]?.text || 'text-gray-700'}`}>
                        {statusStyles[viewingTemplate.status]?.label || viewingTemplate.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Criado em:</span>
                      <span className="font-medium text-xs">
                        {new Date(viewingTemplate.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-700 mb-2">Corpo da Mensagem</h3>
                  <div className="bg-white rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap border border-gray-200 max-h-40 overflow-y-auto">
                                        {viewData.body || 'Sem conteúdo'}
                  </div>
                </div>

                                {viewData.footer && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-700 mb-2">Rodapé</h3>
                    <div className="bg-white rounded-lg p-3 text-sm text-gray-800 border border-gray-200">
                      {viewData.footer}
                    </div>
                  </div>
                )}

                {viewingTemplate.media_path && (
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-700 mb-2">Mídia Anexada</h3>
                    <div className="flex items-center gap-2 bg-white rounded-lg p-2 border border-gray-200">
                                            {viewData.header === 'IMAGE' && <ImageIcon size={20} className="text-green-600" />}
                      {viewData.header === 'VIDEO' && <Video size={20} className="text-blue-600" />}
                      {viewData.header === 'DOCUMENT' && <File size={20} className="text-purple-600" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">
                          {viewingTemplate.media_path.split(/[\\/]/).pop()}
                        </p>
                        <p className="text-[10px] text-gray-500">
                          {viewingTemplate.media_id ? '✅ Enviado para Meta' : '⚠️ Apenas local'}
                        </p>
                      </div>
                    </div>
                    
                                        {viewData.header === 'IMAGE' && viewingTemplateImage && (
                      <div className="mt-3">
                        <img 
                          src={viewingTemplateImage} 
                          alt="Imagem do template" 
                          className="w-full h-auto max-h-[200px] object-contain rounded-lg border border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                <h3 className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                  <Eye size={14} />
                  Prévia
                </h3>
                                <WhatsAppPreview 
                  template={{ header: viewData.header, footer: viewData.footer }}
                  preview={viewData.body}
                  imagePreview={viewingTemplateImage}
                  videoPreview={viewData.header === 'VIDEO' ? viewingTemplate.media_path?.split(/[\\/]/).pop() : null}
                  documentPreview={viewData.header === 'DOCUMENT' ? viewingTemplate.media_path?.split(/[\\/]/).pop() : null}
                  previewButtons={viewData.buttons}
                />                
                                <p className="text-[10px] text-gray-500 mt-2 text-center">
                  💡 Assim o cliente verá
                </p>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
      {/* ============================================ */}
      {/* MODAL DE CRIAÇÃO */}
      {/* ============================================ */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl p-6 relative shadow-xl max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
            <h2 className="text-lg font-bold mb-5">Novo Template</h2>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <form id="templateForm" onSubmit={handleCreate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Nome (slug) *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full p-2 border border-green-400 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                        placeholder="meu_template"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Nome Exibição</label>
                      <input
                        type="text"
                        value={form.display_name}
                        onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                        className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                      />
                    </div>
                  </div>

                  {/* ⭐ NOVO: qual empresa/WABA esse template pertence — essencial quando
                      tem mais de uma conta Meta conectada (uma por setor, por exemplo) */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Empresa / Conta WhatsApp (WABA) *</label>
                    <select
                      value={form.business_id}
                      onChange={(e) => setForm({ ...form, business_id: e.target.value })}
                      className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                      required
                    >
                      <option value="">Selecione setor</option>
                      {businesses.map(n => (
                        <option key={n.id} value={n.business_id}>
                          {n.display_phone_number || n.phone_number} - Setor({n.sector_name || 'Sem setor'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Categoria *</label>
                      <select
                        value={form.categoria}
                        onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                        className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                      >
                        <option value="Utility">Utilidade</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Authentication">Autenticação</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Idioma</label>
                      <select
                        value={form.linguagem}
                        onChange={(e) => setForm({ ...form, linguagem: e.target.value })}
                        className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                      >
                        <option value="pt_BR">Português (BR)</option>
                        <option value="en_US">English (US)</option>
                        <option value="es_ES">Español (ES)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Cabeçalho</label>
                    <select
                      value={form.cabecalho}
                      onChange={(e) => {
                        setForm({ ...form, cabecalho: e.target.value });
                        clearMedia();
                      }}
                      className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                    >
                      <option value="">Nenhum</option>
                      <option value="TEXT">Texto</option>
                      <option value="IMAGE">🖼️ Imagem</option>
                      <option value="VIDEO">🎥 Vídeo</option>
                      <option value="DOCUMENT">📄 Documento</option>
                    </select>
                  </div>

                  {form.cabecalho === 'IMAGE' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Imagem do Cabeçalho</label>
                      
                      <button
                        type="button"
                        onClick={() => setShowAiGenerateModal(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 mb-3"
                      >
                        <Sparkles size={16} />
                        Gerar Imagem com IA
                      </button>

                      {!imagePreview ? (
                        <label className="flex items-center gap-3 px-4 py-2 bg-green-50 border-2 border-dashed border-green-300 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                          <Upload size={18} className="text-green-600" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-green-800">Ou selecione uma imagem</p>
                            <p className="text-xs text-green-600">PNG, JPG (máx. 5MB)</p>
                          </div>
                          <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                        </label>
                      ) : (
                        <div className="relative">
                          <img src={imagePreview} alt="Preview" className="w-full h-24 object-cover rounded-lg border border-green-200" />
                          <button type="button" onClick={() => { setHeaderImage(null); setImagePreview(null); }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-sm">
                            <X size={14} />
                          </button>
                          <p className="text-xs text-green-700 mt-1 truncate">{headerImage?.name}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {form.cabecalho === 'VIDEO' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Vídeo do Cabeçalho</label>
                      {!videoPreview ? (
                        <label className="flex items-center gap-3 px-4 py-2 bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors">
                          <Upload size={18} className="text-blue-600" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-blue-800">Selecionar vídeo</p>
                            <p className="text-xs text-blue-600">MP4 (máx. 16MB)</p>
                          </div>
                          <input type="file" accept="video/*" onChange={handleVideoChange} className="hidden" required />
                        </label>
                      ) : (
                        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-2">
                          <Video size={20} className="text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-blue-900 truncate">{videoPreview}</p>
                            <p className="text-[10px] text-blue-600">Vídeo selecionado</p>
                          </div>
                          <button type="button" onClick={() => { setHeaderVideo(null); setVideoPreview(null); }}
                            className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                            <X size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {form.cabecalho === 'DOCUMENT' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Documento do Cabeçalho</label>
                      {!documentPreview ? (
                        <label className="flex items-center gap-3 px-4 py-2 bg-purple-50 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors">
                          <Upload size={18} className="text-purple-600" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-purple-800">Selecionar documento</p>
                            <p className="text-xs text-purple-600">PDF, DOC (máx. 10MB)</p>
                          </div>
                          <input type="file" accept=".pdf,.doc,.docx" onChange={handleDocumentChange} className="hidden" required />
                        </label>
                      ) : (
                        <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg p-2">
                          <File size={20} className="text-purple-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-purple-900 truncate">{documentPreview}</p>
                            <p className="text-[10px] text-purple-600">Documento selecionado</p>
                          </div>
                          <button type="button" onClick={() => { setHeaderDocument(null); setDocumentPreview(null); }}
                            className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                            <X size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Corpo da Mensagem *
                      <button
                        type="button"
                        onClick={() => setShowVariableSuggestions(!showVariableSuggestions)}
                        className="ml-2 text-purple-600 hover:text-purple-700 text-xs font-medium"
                      >
                        {showVariableSuggestions ? 'Ocultar variáveis' : 'Ver variáveis disponíveis'}
                      </button>
                    </label>
                    
                    {showVariableSuggestions && variables.length > 0 && (
                      <div className="mb-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="text-xs font-bold text-purple-900 mb-2 flex items-center gap-1">
                          <Tag size={12} />
                          Clique para inserir:
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {variables.map(v => (
                            <button
                              key={v.id}
                              type="button"
                              onClick={() => insertVariable(v.name)}
                              className="px-2 py-1 bg-white border border-purple-300 rounded text-xs font-mono text-purple-700 hover:bg-purple-100 transition-colors"
                              title={v.description}
                            >
                              {'{{' + v.name + '}}'}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <textarea
                      name="corpo"
                      value={form.corpo}
                      onChange={(e) => setForm({ ...form, corpo: e.target.value })}
                      rows={5}
                      className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none font-mono"
                      placeholder="Ex: Olá {{nome_cliente}}, sua consulta está marcada para {{data_agendamento}}!"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Use {'{{nome_da_variável}}'} para inserir variáveis dinâmicas
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rodapé</label>
                    <input
                      type="text"
                      value={form.rodape}
                      onChange={(e) => setForm({ ...form, rodape: e.target.value })}
                      className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none"
                    />
                  </div>

                  <div className="border-t border-gray-100 pt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-2">Botões (opcional)</label>
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => { setButtonType('quick_reply'); addButton('quick_reply'); }}
                        disabled={buttons.some(b => b.type === 'PHONE_NUMBER' || b.type === 'URL')}
                        className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        + Resposta Rápida
                      </button>
                      <button
                        type="button"
                        onClick={() => { setButtonType('cta'); addButton('phone'); }}
                        disabled={buttons.some(b => b.type === 'QUICK_REPLY')}
                        className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        + Telefone
                      </button>
                      <button
                        type="button"
                        onClick={() => { setButtonType('cta'); addButton('url'); }}
                        disabled={buttons.some(b => b.type === 'QUICK_REPLY')}
                        className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        + Link
                      </button>
                    </div>

                    {buttons.length > 0 && (
                      <p className="text-xs text-gray-400 mb-2">
                        ⚠️ Não é possível misturar Resposta Rápida com Telefone/Link no mesmo template
                      </p>
                    )}

                    <div className="space-y-2">
                      {buttons.map((btn, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
                          <span className="text-xs font-medium text-gray-500 w-20 flex-shrink-0">
                            {btn.type === 'QUICK_REPLY' ? 'Resposta' : btn.type === 'PHONE_NUMBER' ? 'Telefone' : 'Link'}
                          </span>
                          <input
                            type="text"
                            placeholder={btn.type === 'QUICK_REPLY' ? 'Ex: Eu quero' : 'Texto do botão'}
                            value={btn.text}
                            onChange={(e) => updateButton(idx, 'text', e.target.value)}
                            maxLength={25}
                            className="flex-1 p-1.5 border border-gray-200 rounded text-xs"
                          />
                          {btn.type === 'PHONE_NUMBER' && (
                            <input
                              type="text"
                              placeholder="+5511999998888"
                              value={btn.phone_number}
                              onChange={(e) => updateButton(idx, 'phone_number', e.target.value)}
                              className="flex-1 p-1.5 border border-gray-200 rounded text-xs"
                            />
                          )}
                          {btn.type === 'URL' && (
                            <input
                              type="text"
                              placeholder="https://..."
                              value={btn.url}
                              onChange={(e) => updateButton(idx, 'url', e.target.value)}
                              className="flex-1 p-1.5 border border-gray-200 rounded text-xs"
                            />
                          )}
                          <button type="button" onClick={() => removeButton(idx)} className="text-red-400 hover:text-red-600 text-xs px-1">
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </form>

                <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                  <button type="button" onClick={() => setShowModal(false)}
                    className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50">
                    Cancelar
                  </button>
                  <button type="submit" form="templateForm"
                    disabled={loading || (form.cabecalho === 'IMAGE' && !headerImage) || (form.cabecalho === 'VIDEO' && !headerVideo) || (form.cabecalho === 'DOCUMENT' && !headerDocument)}
                    className="px-5 py-2 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                    {loading ? 'Salvando...' : 'Criar Template'}
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                <h3 className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
                  <FileText size={14} />
                  Prévia
                </h3>
                                <WhatsAppPreview 
                  template={form}
                  preview={preview}
                  imagePreview={imagePreview}
                  videoPreview={videoPreview}
                  documentPreview={documentPreview}
                  previewButtons={buttons}
                />
                <p className="text-[10px] text-gray-500 mt-2 text-center">
                  💡 Assim o cliente verá
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* ⭐⭐⭐ MODAL DE GERAÇÃO COM IA + ÁUDIO (COMPACTO) ⭐⭐⭐ */}
      {/* ============================================ */}
      {showAiGenerateModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Sparkles size={20} />
                  Gerar Imagem com IA
                </h2>
                <p className="text-xs opacity-90 mt-0.5">
                  Fale ou digite o que você quer
                </p>
              </div>
              <button
                onClick={() => {
                  if (isRecording) stopRecording();
                  clearAiFields();
                  setShowAiGenerateModal(false);
                }}
                className="p-1.5 hover:bg-white/20 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* ⭐ SEÇÃO DE GRAVAÇÃO DE ÁUDIO */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold text-purple-900 flex items-center gap-1.5">
                    <Mic size={14} />
                    Gravação por Voz
                  </h3>
                  {isRecording && (
                    <span className="flex items-center gap-1 text-[10px] text-red-600 font-bold">
                      <div className="w-1.5 h-1.5 bg-red-600 rounded-full animate-pulse"></div>
                      {formatTime(recordingTime)}
                    </span>
                  )}
                </div>

                <div className="flex gap-2 mb-2">
                  {!isRecording ? (
                    <button
                      type="button"
                      onClick={startRecording}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg text-xs font-medium hover:from-red-600 hover:to-red-700 shadow-lg shadow-red-200"
                    >
                      <Mic size={14} />
                      Iniciar
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={stopRecording}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-lg text-xs font-medium hover:from-gray-800 hover:to-gray-900 shadow-lg"
                    >
                      <Square size={14} />
                      Parar
                    </button>
                  )}
                </div>
                
                {audioTranscript && (
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        console.log(' Interpretando áudio...');
                        console.log('Texto:', audioTranscript);
                        interpretAudioAndFill();
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg text-xs font-medium hover:from-green-600 hover:to-green-700 shadow-lg shadow-green-200"
                    >
                      <Sparkles size={14} />
                      Interpretar
                    </button>
                    <button
                      type="button"
                      onClick={clearAiFields}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-500 text-white rounded-lg text-xs font-medium hover:bg-gray-600 shadow-lg"
                      title="Limpar tudo"
                    >
                      <X size={14} />
                      Limpar
                    </button>
                  </div>
                )}

                {isRecording && (
                  <div className="flex items-center justify-center gap-0.5 py-2 bg-white/50 rounded-lg">
                    {[...Array(15)].map((_, i) => (
                      <div
                        key={i}
                        className="w-0.5 bg-gradient-to-t from-purple-500 to-pink-500 rounded-full animate-pulse"
                        style={{
                          height: `${Math.random() * 20 + 8}px`,
                          animationDelay: `${i * 0.1}s`,
                          animationDuration: '0.5s'
                        }}
                      />
                    ))}
                  </div>
                )}

                {audioTranscript && (
                  <div className="mt-2">
                    <label className="block text-[10px] font-medium text-purple-900 mb-0.5">
                      O que você disse:
                    </label>
                    <div className="bg-white rounded-lg p-2 border border-purple-200">
                      <p className="text-xs text-gray-800 italic">"{audioTranscript}"</p>
                    </div>
                  </div>
                )}

                <p className="text-[10px] text-purple-700 mt-1.5">
                  💡 Ex: "Promoção de Natal para dentista, azul e branco, moderno"
                </p>
              </div>

              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-[10px] text-gray-500 font-medium">OU PREENCHA MANUALMENTE</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              {/* Campos em grid compacto */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    <Palette size={12} className="inline mr-1" />
                    Tipo de Campanha
                  </label>
                  <input
                    type="text"
                    value={aiGenerateForm.campaign_type}
                    onChange={e => {
                      console.log('📝 Campaign type:', e.target.value);
                      setAiGenerateForm({...aiGenerateForm, campaign_type: e.target.value});
                    }}
                    placeholder="Ex: Promoção de Natal"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    <Building2 size={12} className="inline mr-1" />
                    Negócio/Produto
                  </label>
                  <input
                    type="text"
                    value={aiGenerateForm.business_type}
                    onChange={e => {
                      console.log(' Business type:', e.target.value);
                      setAiGenerateForm({...aiGenerateForm, business_type: e.target.value});
                    }}
                    placeholder="Ex: Consultório Odontológico"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Estilo</label>
                  <select
                    value={aiGenerateForm.style}
                    onChange={e => {
                      console.log('📝 Style:', e.target.value);
                      setAiGenerateForm({...aiGenerateForm, style: e.target.value});
                    }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 outline-none"
                  >
                    <option value="">Selecione</option>
                    <option value="Moderno e profissional">Moderno</option>
                    <option value="Minimalista e clean">Minimalista</option>
                    <option value="Colorido e vibrante">Colorido</option>
                    <option value="Elegante e sofisticado">Elegante</option>
                    <option value="Divertido e descontraído">Divertido</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Cores</label>
                  <input
                    type="text"
                    value={aiGenerateForm.colors.join(', ')}
                    onChange={e => {
                      console.log('📝 Colors:', e.target.value);
                      setAiGenerateForm({
                        ...aiGenerateForm, 
                        colors: e.target.value.split(',').map(c => c.trim()).filter(c => c)
                      });
                    }}
                    placeholder="Azul, Branco"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">
                    <Type size={12} className="inline mr-1" />
                    Texto Principal
                  </label>
                  <input
                    type="text"
                    value={aiGenerateForm.main_text}
                    onChange={e => {
                      console.log('📝 Main text:', e.target.value);
                      setAiGenerateForm({...aiGenerateForm, main_text: e.target.value});
                    }}
                    placeholder="Ex: Sorriso perfeito para 2024"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Elementos Adicionais</label>
                  <textarea
                    value={aiGenerateForm.additional_elements}
                    onChange={e => setAiGenerateForm({...aiGenerateForm, additional_elements: e.target.value})}
                    placeholder="Ex: Ícones de dentes, estrelas"
                    rows={1}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    if (isRecording) stopRecording();
                    clearAiFields();
                    setShowAiGenerateModal(false);
                  }}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    console.log(' Gerando imagem...');
                    console.log('Form:', aiGenerateForm);
                    handleGenerateWithAi();
                  }}
                  disabled={generating || !aiGenerateForm.campaign_type}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg text-xs font-bold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} />
                      Gerar Imagem
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Templates;
